import { UseFetchInfiniteHookProps } from "../models/UseFetchInfiniteHookProps";
import { PageOf } from "../models/PageOf";
import { JSONCensusEquipment } from "../models/JSONCensusEquipment";
import {
	fetchCensusEntriesByInventoryStatus,
	PageOfCensusEquipmentParam,
} from "../controllers/CensusCoolerController";
import useSWRInfinite from "swr/infinite";
import * as React from "react";
import { concatenateApiPaginatedResponse } from "../utils";

export type UseFetchCensusEntriesListInfiniteHookProps =
	UseFetchInfiniteHookProps<PageOf<JSONCensusEquipment>, JSONCensusEquipment>;

export const useCensusEntriesByInventoryStatusHook = (
	params: PageOfCensusEquipmentParam,
): UseFetchCensusEntriesListInfiniteHookProps => {
	const swrKey = (index: number) => ({
		...params,
		page: index,
	});

	const { data, error, setSize, isLoading, isValidating, size, mutate } =
		useSWRInfinite<PageOf<JSONCensusEquipment>, Error>(
			swrKey,
			fetchCensusEntriesByInventoryStatus,
			{
				revalidateFirstPage: false,
				revalidateAll: false,
				revalidateIfStale: false,
				revalidateOnFocus: true,
			},
		);

	const isFetching = isLoading;

	const concatenatedData = React.useMemo(
		() => (data ? concatenateApiPaginatedResponse(data) : []),
		[data],
	);

	const isNextPageLoading = isValidating;

	const handleOnLoadNextPage = async (startIndex?: number) => {
		if (startIndex === undefined) {
			await setSize(0);
		}
		await setSize(startIndex + 1);
	};

	return {
		mutate,
		lastPageInfo: data?.[data?.length - 1],
		flattenedData: concatenatedData.length ? concatenatedData : [],
		error,
		isNextPageLoading,
		isFetching,
		handleOnLoadNextPage,
		totalCount: data?.[0]?.totalElements ?? 0,
		size: params?.size,
		hasNextPage: data?.[data?.length - 1]?.last === false,
	};
};
